import { Container } from 'react-bootstrap';
import ManualBreadcrumb from '../../components/manual/ManualBreadcrumb';
import ManualSection from '../../components/manual/ManualSection';
import ManualStep from '../../components/manual/ManualStep';
import ManualAlert from '../../components/manual/ManualAlert';

function ManualAtendimentosPage() {
  return (
    <Container fluid className="py-4">
      <ManualBreadcrumb 
        items={[
          { label: 'Manual', path: '/painel/manual' },
          { label: 'Atendimentos', path: '/painel/manual/atendimentos' }
        ]}
      />

      <div className="manual-header">
        <h1 className="manual-title">
          <i className="bi bi-clipboard-check me-3"></i>
          Atendimentos
        </h1>
        <p className="manual-subtitle">
          Aprenda a registrar e gerenciar os atendimentos prestados aos adolescentes
        </p>
      </div>

      <ManualAlert type="info">
        <strong>O que são Atendimentos?</strong> São os registros de serviços e acompanhamentos 
        realizados durante uma movimentação do adolescente. Eles documentam cada ação tomada 
        pela equipe técnica durante o período em que o adolescente está sob custódia.
      </ManualAlert>

      <ManualSection icon="bi-clipboard-list" title="Registrando Atendimento">
        <p className="mb-4">
          Os atendimentos são registrados dentro de uma movimentação. No dashboard, cada linha de
          movimentação exibe cartões clicáveis para Promotoria, Saúde, Educação, Defensoria/Adv
          e Especialistas.
        </p>

        <ManualStep number={1}>
          <div>
            <strong>Acesse o Dashboard</strong>
            <p className="mb-0">
              Use os filtros de status, data e busca para localizar a movimentação desejada.
            </p>
          </div>
        </ManualStep>

        <ManualStep number={2}>
          <div>
            <strong>Localize o cartão do atendimento</strong>
            <p className="mb-0">
              Clique no cartão da área responsável. Se o cartão estiver bloqueado, verifique se
              a movimentação é MBA, se está finalizada, se é fim de semana/feriado ou se seu perfil
              possui a permissão necessária.
            </p>
          </div>
        </ManualStep>

        <ManualStep number={3}>
          <div>
            <strong>Preencha a janela da área</strong>
            <p className="mb-0">
              Cada área possui campos próprios. Ao salvar, o cartão é atualizado e os demais
              usuários recebem a atualização em tempo real.
            </p>
          </div>
        </ManualStep>

        <ManualStep number={4}>
          <div>
            <strong>Revise entrada e saída</strong>
            <div className="mt-2 manual-service-card">
              <p className="mb-2"><strong>Campos comuns:</strong></p>
              <ul className="mb-0">
                <li><strong>Entrada:</strong> marca que a área atendeu na entrada</li>
                <li><strong>Saída:</strong> marca que a área concluiu ou atendeu na saída</li>
                <li><strong>Observações:</strong> registram informações complementares quando disponíveis</li>
                <li><strong>SEI:</strong> indica abertura ou vínculo de processo SEI nas áreas que usam esse controle</li>
              </ul>
            </div>
          </div>
        </ManualStep>

        <ManualStep number={5}>
          <div>
            <strong>Salve</strong>
            <p className="mb-0">
              Clique em <strong>Salvar</strong>. Se a movimentação já tiver atendimento daquela área,
              o sistema atualiza o registro existente.
            </p>
          </div>
        </ManualStep>
      </ManualSection>

      <ManualSection icon="bi-clock-history" title="Tipos de Atendimento">
        <p className="mb-4">
          O sistema trabalha com cinco áreas de atendimento, cada uma com regras próprias:
        </p>

        <div className="row g-3 mb-4">
          <div className="col-md-6">
            <div className="manual-service-card">
              <h6 className="text-success mb-2">
                <i className="bi bi-clipboard-check me-2"></i>
                Promotoria
              </h6>
              <p className="mb-0 small">
                Registra promotor responsável, horário, entrada, saída e observações. Fica bloqueado
                para movimentações com meio de entrada MBA.
              </p>
            </div>
          </div>

          <div className="col-md-6">
            <div className="manual-service-card">
              <h6 className="text-success mb-2">
                <i className="bi bi-clipboard-check me-2"></i>
                Defensoria/Adv
              </h6>
              <p className="mb-0 small">
                Registra nome do representante, entrada, saída e observações. Continua disponível
                em fins de semana e feriados para perfis autorizados.
              </p>
            </div>
          </div>

          <div className="col-md-6">
            <div className="manual-service-card">
              <h6 className="text-success mb-2">
                <i className="bi bi-clipboard-check me-2"></i>
                Saúde
              </h6>
              <p className="mb-0 small">
                Registra entrada, saída e processo SEI. Fica bloqueado em fins de semana e feriados
                para perfis que não sejam Promotoria ou Defensoria.
              </p>
            </div>
          </div>

          <div className="col-md-6">
            <div className="manual-service-card">
              <h6 className="text-success mb-2">
                <i className="bi bi-clipboard-check me-2"></i>
                Educação
              </h6>
              <p className="mb-0 small">
                Registra entrada, saída e processo SEI. Fica bloqueado em fins de semana/feriados
                e também para movimentações MBA.
              </p>
            </div>
          </div>

          <div className="col-md-6">
            <div className="manual-service-card">
              <h6 className="text-success mb-2">
                <i className="bi bi-clipboard-check me-2"></i>
                Especialistas
              </h6>
              <p className="mb-0 small">
                Registra especialista, entrada, saída, SEI, responsável presente, nome do responsável
                e observações. Também respeita a restrição de fim de semana/feriado.
              </p>
            </div>
          </div>
        </div>
      </ManualSection>

      <ManualSection icon="bi-pencil-square" title="Visualizando e Editando Atendimentos">
        <ManualStep number={1}>
          <div>
            <strong>Visualizar Detalhes</strong>
            <p className="mb-0">
              Na lista de atendimentos da movimentação, clique no ícone de visualização (olho) 
              para ver todos os detalhes do atendimento registrado.
            </p>
          </div>
        </ManualStep>

        <ManualStep number={2}>
          <div>
            <strong>Editar Atendimento</strong>
            <p className="mb-0">
              Clique no ícone de edição (lápis) para alterar as informações de um atendimento. 
              Você pode corrigir erros ou adicionar informações complementares.
            </p>
          </div>
        </ManualStep>

        <ManualStep number={3}>
          <div>
            <strong>Histórico de Alterações</strong>
            <p className="mb-0">
              O sistema mantém um registro de todas as alterações realizadas em um atendimento, 
              incluindo data, hora e usuário responsável pela modificação.
            </p>
          </div>
        </ManualStep>

        <ManualAlert type="warning">
          <strong>Atenção:</strong> Apenas usuários com permissão adequada podem criar, editar ou
          excluir atendimentos. Usuários com apenas <code>atendimentos.view</code> conseguem abrir
          os registros para consulta, mas não salvam alterações.
        </ManualAlert>
      </ManualSection>

      <ManualSection icon="bi-calendar-x" title="Restrições de Fim de Semana e Feriado">
        <ManualAlert type="important">
          Atendimentos de <strong>Saúde</strong>, <strong>Educação</strong> e <strong>Especialistas</strong>
          são bloqueados em fins de semana e feriados nacionais para perfis que não contenham
          <strong> Promotoria</strong> ou <strong>Defensoria</strong> no nome do perfil. Essa regra
          vale inclusive para administradores.
        </ManualAlert>
      </ManualSection>

      <ManualSection icon="bi-exclamation-triangle" title="Boas Práticas">
        <div className="manual-help-box">
          <h6 className="text-warning mb-3">Dicas importantes:</h6>
          <ul className="mb-0">
            <li className="mb-2">
              <strong>Seja detalhado:</strong> Quanto mais informações você registrar, melhor 
              será o acompanhamento do adolescente
            </li>
            <li className="mb-2">
              <strong>Use linguagem técnica:</strong> Os atendimentos podem ser usados em 
              relatórios oficiais, portanto use terminologia apropriada
            </li>
            <li className="mb-2">
              <strong>Registre imediatamente:</strong> Faça o registro logo após o atendimento 
              para não esquecer detalhes importantes
            </li>
            <li className="mb-2">
              <strong>Revise antes de salvar:</strong> Verifique se todas as informações 
              estão corretas, especialmente data, hora e nome do adolescente
            </li>
            <li className="mb-2">
              <strong>Proteja informações sensíveis:</strong> Evite expor dados muito pessoais 
              ou sensíveis que não sejam estritamente necessários
            </li>
          </ul>
        </div>
      </ManualSection>

      <ManualAlert type="tip">
        <strong>Dica:</strong> Você pode filtrar os atendimentos por tipo, data ou profissional 
        na página de detalhes da movimentação. Use os filtros para encontrar rapidamente o 
        atendimento que procura.
      </ManualAlert>
    </Container>
  );
}

export default ManualAtendimentosPage;
